import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge, estimateSuffix, defaultNumberFormat } from "../../components/index.ts";
import type { DecisionSummary } from "./use-decisions.ts";
import {
  quantityMeasure,
  failClosedLevel,
  failClosedLabel,
  failClosedHref,
  FAIL_CLOSED_REASONS,
} from "./vocab.ts";

/**
 * Fail-closed events, highlighted (task 11.6; design 12.2 "fail-closed events highlighted"; FR-3.3;
 * 12.3 "fail-closed events highlighted"; 12.4 "status by icon + text, never colour alone").
 *
 * This is THE named behaviour of the screen: a fail-closed decision — one the boundary could not
 * reach (`decision_unavailable`) or could not verify (`decision_unverifiable`) — is surfaced
 * distinctly from an ordinary deny, in a prominent panel at the top of the screen, with a
 * high-visibility affordance that is legible WITHOUT colour (a warning glyph via `StatusBadge` plus
 * the word "Fail-closed" and the count). A non-zero count reads as a warning (never healthy green);
 * zero reads neutral; a null count is grey "unverifiable", never green.
 *
 * The contract splits `totals.fail_closed` along exactly the two fail-closed reason codes, and both
 * are real `/events` `reason` filter values, so each sub-count drills straight to the events behind
 * it (`decision=deny&reason=…`). The totals carry no server `drill`, so the screen builds these
 * honest standing filters itself (see `vocab.failClosedHref`) rather than inventing a route.
 */

type FailClosed = DecisionSummary["totals"]["fail_closed"];

export function FailClosedPanel(props: { failClosed: FailClosed }): ReactNode {
  const navigate = useNavigate();
  const { failClosed } = props;

  // One entry per fail-closed reason, read from the two contract sub-counts. The total is the sum of
  // the known sub-counts; if either is null (no data) the total is treated as unverifiable.
  const entries = FAIL_CLOSED_REASONS.map((reason) => {
    const key = reason === "decision_unavailable" ? "decision_unavailable" : "decision_unverifiable";
    const q = failClosed[key];
    return { reason, value: q.value, measure: quantityMeasure(q) } as const;
  });

  const anyNull = entries.some((e) => e.value === null);
  const totalValue = anyNull ? null : entries.reduce((sum, e) => sum + (e.value ?? 0), 0);
  const headlineLevel = failClosedLevel(totalValue);
  const headlineLabel =
    totalValue === null
      ? "Fail-closed: unknown"
      : totalValue > 0
        ? `Fail-closed: ${defaultNumberFormat(totalValue)}`
        : "Fail-closed: none";

  return (
    <section className="dec-failclosed" aria-label="Fail-closed decisions" data-active={totalValue !== null && totalValue > 0}>
      <div className="dec-failclosed__head">
        <StatusBadge
          level={headlineLevel}
          label={headlineLabel}
          title={
            totalValue === null
              ? "Fail-closed decisions could not be counted in this window."
              : totalValue > 0
                ? `${defaultNumberFormat(totalValue)} fail-closed decision(s) in this window. The boundary denied because it could not reach or verify a decision.`
                : "No fail-closed decisions in this window."
          }
        />
        <p className="dec-failclosed__blurb">
          A fail-closed decision is a deny the boundary made because it could not reach or verify a
          decision, distinct from an ordinary policy deny.
        </p>
      </div>

      <ul className="dec-failclosed__reasons">
        {entries.map((e) => {
          const display = e.value === null ? "n/a" : `${defaultNumberFormat(e.value)}${estimateSuffix(e.measure)}`;
          const canDrill = e.value !== null && e.value > 0;
          return (
            <li key={e.reason} className="dec-failclosed__reason" data-measure={e.measure}>
              <span className="dec-failclosed__reason-label">{failClosedLabel(e.reason)}</span>
              {canDrill ? (
                <button
                  type="button"
                  className="dec-link dec-link--strong"
                  onClick={() => navigate(failClosedHref(e.reason))}
                  aria-label={`${failClosedLabel(e.reason)}: ${display}. Open the events behind these fail-closed decisions`}
                >
                  {display}
                </button>
              ) : (
                <span className="dec-num" data-measure={e.measure}>
                  {display}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
